import { Minus, Plus, Trash2, ArrowRight, FileDown } from "lucide-react";
import { useCart, unitPrice, tierLabel } from "@/lib/cart";
import VialImage from "@/components/VialImage";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, setQty, remove, subtotal, drawerOpen, setDrawerOpen, setCheckoutOpen } = useCart();
  const shipping = subtotal >= 300 || subtotal === 0 ? 0 : 15;

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent side="right" className="w-full sm:max-w-md rounded-none flex flex-col" data-testid="cart-drawer-container">
        <SheetHeader>
          <SheetTitle className="font-display text-xl">Order Request</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto mt-4 space-y-4 pr-1">
          {items.length === 0 && (
            <p className="text-sm text-slate-500 py-12 text-center" data-testid="cart-empty">
              No compounds in your order request yet.
            </p>
          )}
          {items.map(({ product: p, qty }) => (
            <div key={p.id} className="border border-slate-200 p-4" data-testid={`cart-item-${p.id}`}>
              <div className="flex gap-3">
                <VialImage product={p} className="h-20 shrink-0" />
                <div className="flex-1 flex items-start justify-between gap-2">
                  <div>
                    <p className="font-display font-semibold text-sm text-slate-900">{p.name}</p>
                    <p className="font-tech text-[11px] text-slate-500 mt-0.5">{p.specification} · {p.sku}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      <span className="inline-block font-tech text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5">
                        {tierLabel(qty)}
                      </span>
                      {p.has_coa_file && (
                        <a href={`${API}/coa/${p.id}`} target="_blank" rel="noopener noreferrer" data-testid={`cart-item-coa-link-${p.id}`}
                          className="inline-flex items-center gap-1 font-tech text-[10px] uppercase tracking-wide text-emerald-700 hover:text-emerald-900 transition-colors">
                          <FileDown className="h-3 w-3" /> COA{p.coa_lot ? ` ${p.coa_lot}` : ""}
                        </a>
                      )}
                    </div>
                  </div>
                  <button onClick={() => remove(p.id)} data-testid={`cart-item-remove-btn-${p.id}`}
                    className="text-slate-400 hover:text-red-600 transition-colors h-fit" aria-label="Remove">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-center border border-slate-300">
                  <button onClick={() => setQty(p.id, qty - 1)} data-testid={`cart-item-decrement-btn-${p.id}`}
                    className="p-1.5 hover:bg-slate-100 transition-colors" aria-label="Decrease">
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="font-tech text-sm w-10 text-center">{qty}</span>
                  <button onClick={() => setQty(p.id, qty + 1)} data-testid={`cart-item-increment-btn-${p.id}`}
                    className="p-1.5 hover:bg-slate-100 transition-colors" aria-label="Increase">
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="font-tech text-sm font-semibold text-slate-900">
                  ${(unitPrice(p, qty) * qty).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>
        {items.length > 0 && (
          <div className="border-t border-slate-200 pt-4 mt-4 space-y-2">
            <div className="flex justify-between text-sm text-slate-600">
              <span>Subtotal</span><span className="font-tech" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-slate-600">
              <span>Insured Lab Freight {shipping === 0 && "(free ≥ $300)"}</span>
              <span className="font-tech" data-testid="cart-shipping">${shipping.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-slate-100">
              <span>Estimated Total</span><span className="font-tech" data-testid="cart-total">${(subtotal + shipping).toFixed(2)}</span>
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed pt-1">
              Nothing is charged yet. After you sign the Research Purchaser Agreement you can pay securely by card
              or request a commercial invoice. Research Use Only.
            </p>
            <Button onClick={() => { setDrawerOpen(false); setCheckoutOpen(true); }}
              data-testid="cart-drawer-checkout-btn"
              className="w-full gap-2 mt-2">
              Proceed to Compliance &amp; Inquiry Checkout <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
